import { geminiRateLimiter } from '../lib/rateLimiter';
import { supabase } from '../lib/supabase';

/**
 * Auth headers for the Cloud Function API.
 *
 * Every /api route sits behind requireAuth (functions/src/index.ts), which
 * expects the Supabase access token as a Bearer header. Without it the request
 * is rejected with 401 before it ever reaches Gemini.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Your session has expired. Please sign in again.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
  };
}

// All Gemini calls go through the Cloud Function proxy — the API key never
// touches the browser. The base URL is empty in production (same origin via
// Firebase Hosting rewrites); in development the emulator runs on :5001.
const API_BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL ?? '';

interface GenerationRequest {
  docType: string;
  sections: string[];
  contentValues: Record<string, string>;
  fieldLabels: Record<string, string>;
}

export const geminiService = {
  // Constructs the prompt — same logic as before, kept client-side since it
  // contains no secrets (it's just a template, not an API call).
  buildPrompt(request: GenerationRequest): string {
    const { docType, sections, contentValues, fieldLabels } = request;

    let userInputContext = '';
    Object.entries(contentValues).forEach(([fieldId, value]) => {
      const label = fieldLabels[fieldId] || fieldId;
      if (value?.trim()) userInputContext += `- ${label}: ${value}\n`;
    });

    return `
      You are an expert Product Manager and Business Analyst assistant.
      Your task is to write a detailed, professional ${docType} based on the following inputs.

      DOCUMENT STRUCTURE:
      The document must contain the following sections:
      ${sections.map(s => `- ${s}`).join('\n')}

      USER INPUT CONTEXT:
      ${userInputContext || 'No specific details provided. Use industry best practices for a generic SaaS product.'}

      INSTRUCTIONS:
      1. For each section in the structure, write comprehensive, detailed content.
      2. Use the User Input Context to tailor the content. If details are missing, make reasonable, professional assumptions for a high-quality SaaS product.
      3. "Deep Think": Analyze the requirements thoroughly. Provide specific examples, acceptance criteria, edge cases, and strategic insights where appropriate.
      4. Format the output as clean HTML.
      5. Use <h2> for section titles (matching the requested sections exactly).
      6. Use <p>, <ul>, <li>, <strong>, etc., for formatting content.
      7. Do NOT wrap the entire output in markdown code blocks (like \`\`\`html). Just return the raw HTML string.
      8. Ensure there is an empty <p><br></p> after each section's content to allow for easy editing.

      MERMAID DIAGRAMS - STRICT RULES:
      9. You SHOULD include 2-4 Mermaid diagrams throughout the document where they add value
      9a. Prioritize diagrams in sections like: User Flows, System Architecture, Data Models, Process Workflows, State Transitions, Feature Interactions
      10. CRITICAL - Follow these rules EXACTLY or the diagram will fail:
          - Use ONLY "graph TD" or "graph LR" (no other diagram types)
          - Node IDs must be single letters: A, B, C, D, E, F, G, H, I, J
          - Node labels must be SHORT (max 2-3 words)
          - NO colons, NO parentheses, NO quotes in labels
          - NO subgraphs, NO styling, NO classes
          - Use only --> for arrows (no other arrow types)
          - Maximum 6 nodes per diagram
          - Format: <div class="mermaid-diagram"><pre class="mermaid">DIAGRAM_CODE</pre></div>

      11. VALID Example:
          <div class="mermaid-diagram">
          <pre class="mermaid">
          graph TD
              A[User Login] --> B[Check Credentials]
              B --> C{Valid}
              C -->|Yes| D[Dashboard]
              C -->|No| E[Error]
          </pre>
          </div>

      12. INVALID Examples (DO NOT USE):
          - graph TD; A[Login: Enter Details] (has colon)
          - graph TD; A(Rounded) (uses parentheses)
          - subgraph Main ... end (uses subgraph)
          - A[Very Long Description Text] (too long)

      EXAMPLE OUTPUT FORMAT:
      <h2>Section Name</h2>
      <p>Detailed content goes here...</p>
      <div class="mermaid-diagram">
      <pre class="mermaid">
      graph TD
          A[Start] --> B[Process]
          B --> C[End]
      </pre>
      </div>
      <p>More explanation...</p>
      <ul>
        <li>Point 1</li>
        <li>Point 2</li>
      </ul>
      <p><br></p>
      <h2>Next Section</h2>
      ...
    `;
  },

  formatResponse(text: string): string {
    let clean = text.replace(/```html/g, '').replace(/```/g, '').trim();
    clean = clean
      .replace(/<!DOCTYPE html>/i, '')
      .replace(/<html>/i, '')
      .replace(/<body>/i, '')
      .replace(/<\/body>/i, '')
      .replace(/<\/html>/i, '');
    return clean;
  },

  async generateDocumentContent(request: GenerationRequest): Promise<string> {
    geminiRateLimiter.acquire();
    const prompt = this.buildPrompt(request);

    const res = await fetch(`${API_BASE}/api/gemini/generate`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ prompt }),
    });
    if (!res.ok) throw new Error('Document generation failed');
    const { text } = await res.json() as { text: string };
    return this.formatResponse(text);
  },

  async *generateDocumentStream(request: GenerationRequest): AsyncGenerator<string, void, unknown> {
    geminiRateLimiter.acquire();
    const prompt = this.buildPrompt(request);

    const res = await fetch(`${API_BASE}/api/gemini/stream`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ prompt }),
    });
    if (!res.ok) throw new Error('Stream request failed');
    if (!res.body) throw new Error('No response body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const payload = line.slice(6);
        if (payload === '[DONE]') return;
        try {
          const parsed = JSON.parse(payload) as { text?: string; error?: string };
          if (parsed.error) throw new Error(parsed.error);
          if (parsed.text) yield parsed.text;
        } catch { /* skip malformed SSE lines */ }
      }
    }
  },

  async convertTranscriptionToNotes(transcription: string): Promise<{
    summary: string;
    highlights: string;
    actionItems: string;
  }> {
    if (!transcription?.trim()) throw new Error('No transcription available to convert');
    geminiRateLimiter.acquire();

    const res = await fetch(`${API_BASE}/api/gemini/transcribe-notes`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ transcription }),
    });
    if (!res.ok) throw new Error('Transcription notes conversion failed');
    return res.json();
  },

  async transcribeAudio(audioBlob: Blob): Promise<string> {
    geminiRateLimiter.acquire();

    // Convert blob to base64 on the client, send to backend for the Gemini call
    const base64Audio = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });

    const res = await fetch(`${API_BASE}/api/gemini/transcribe-audio`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ audioBase64: base64Audio, mimeType: audioBlob.type || 'audio/webm' }),
    });
    if (!res.ok) throw new Error('Audio transcription failed');
    const { text } = await res.json() as { text: string };
    return text;
  },
};
